/**
 * The Dashboard's page row, built from the `/review` cards (TECHNICAL_DEBT §0ck).
 *
 * Owner's rule (2026-10-29): when a page has a card on `/review`, the Dashboard's row for that page
 * says so too, and the Dashboard says nothing `/review` does not. The row used to be a second copy of
 * the card rules, written in the component one predicate at a time, and every card added after
 * sprint 2 went into the first copy only: on the frozen sprint 8 data the two disagreed 20 times on
 * ManyRequests and 10 on SlideSpeak.
 *
 * So the rows are now read off `reviewPageCards()` and nothing else: one row per card the belt
 * renders (`REVIEW_CARD_ON_BELT`), with the card's own count and verdict. Action cards, the held-back
 * comparison among them, are attention rows. The note on why the section comparison is blank is one
 * short sentence in the panel's "Not checked" line. Fields the belt has no card for (export only)
 * have no row, and neither does a card `/review` hides (the owner's no-card rule: it is null).
 *
 * `DASHBOARD_ROW_BUILDERS` is keyed by every field, null where the belt has no card: a new field
 * without a decision here fails to compile, and `dashboardRowParity` is what the free PR check runs
 * over the frozen fixtures, against what the belt renders, so a card that slips through anyway (or a
 * row for a field the belt does not render) fails the check.
 *
 * Client-facing wording: plain, short, no internal section numbers.
 */

import { missingSectionsCountLabel, missingSectionsFact } from './missing-sections-summary';
import type { EvidenceTone, PageEvidence } from './page-evidence';
import type { ReviewPageCards } from './review-page-model';
import { REVIEW_CARD_ON_BELT, reviewBeltShowsCard, reviewCardShown, type ReviewCardKey } from './review-belt-cards';
import { technicalEvidenceCopy } from './technical-evidence';
import { NEAR_MISS_CARD_TITLE } from './near-miss-card';

/** Section headings, pages and terms are long; three is a glance, more is a list to read. */
export const DASHBOARD_ROW_MAX_CHIPS = 3;
/** Link sources are short paths, so a couple more fit before the row stops being scannable. */
export const DASHBOARD_LINK_MAX_CHIPS = 4;

export { reviewCardShown, type ReviewCardKey };

/** One Dashboard line, and the card it stands for. */
export interface DashboardPageRow extends PageEvidence {
  /** The `/review` card this row is. */
  card: ReviewCardKey;
  /** The card's own count: items, pages, sections, pairs; 1 for a card that is one finding. */
  count: number;
}

function plural(n: number, one: string, many: string): string {
  return n === 1 ? `1 ${one}` : `${n} ${many}`;
}

function chips(items: string[], max = DASHBOARD_ROW_MAX_CHIPS): string[] {
  const clean = items.map((s) => (s || '').trim()).filter(Boolean);
  return clean.slice(0, max).concat(clean.length > max ? [`+${clean.length - max}`] : []);
}

function sentence(text: string): string {
  const t = text.trim();
  return /[.!?…]$/.test(t) ? t : `${t}.`;
}

function pathOf(url: string): string {
  try {
    return new URL(url).pathname || '/';
  } catch {
    return url;
  }
}

/** An action card: an attention row. A card's own `bad` stays red; everything else is to do. */
function action(
  card: ReviewCardKey,
  key: string,
  title: string,
  count: number,
  label: string,
  fact: string,
  specifics: string[],
  tone: EvidenceTone = 'warn'
): DashboardPageRow {
  return { card, key, title, label, tone: tone === 'bad' ? 'bad' : 'warn', fact, specifics, payload: null, count };
}

/** An explanation card: one sentence in the "Not checked" line. */
function note(card: ReviewCardKey, title: string, fact: string, count = 1): DashboardPageRow {
  return { card, key: `note:${card}`, title, label: '', tone: 'muted', fact, specifics: [], payload: null, count };
}

function technicalRow(
  card: 'technicalIssues' | 'technicalWarnings',
  findings: NonNullable<ReviewPageCards['technicalIssues']>
): DashboardPageRow {
  const critical = card === 'technicalIssues';
  // The card tags each finding with its scope; no tag means the domain rollup has not answered.
  const known = findings.every((f) => f.scope !== null);
  const copy = technicalEvidenceCopy({
    issues: findings.map((f) => ({ code: f.code, title: f.title, details: [] })),
    siteWideCodes: known ? new Set(findings.filter((f) => f.scope === 'Site-wide').map((f) => f.code)) : null,
    severity: critical ? 'critical' : 'warning'
  });
  return action(
    card,
    critical ? 'technical_critical' : 'technical_warnings',
    critical ? 'Technical issues' : 'Technical warnings',
    findings.length,
    copy.label,
    copy.fact,
    copy.specifics,
    copy.tone
  );
}

type RowBuilders = {
  [K in ReviewCardKey]-?: ((card: NonNullable<ReviewPageCards[K]>) => DashboardPageRow) | null;
};

/**
 * Every card's row, in `/review`'s order; null for a field the belt renders no card for
 * (`REVIEW_CARD_ON_BELT`), which therefore has no Dashboard row. Keyed by every field of
 * `ReviewPageCards`, so adding a card without deciding its Dashboard line does not compile.
 */
export const DASHBOARD_ROW_BUILDERS: RowBuilders = {
  // The belt's "Search intent" card: its badge (RE-TARGET, OTHER SUBJECT, BRAND SEARCH…) and its
  // one-line verdict, with the searches to aim at instead.
  comparisonWithheld: (n) =>
    action(
      'comparisonWithheld',
      'serp_withheld',
      'Search intent',
      1,
      n.badge,
      sentence(n.card.verdictLine),
      // Under the card's own label, as the belt lists them, so the searches never read bare.
      chips((n.card.instead?.rows ?? []).map((r) => r.query)).map((chip, i) =>
        i === 0 && n.card.instead ? `${n.card.instead.label}: ${chip}` : chip
      )
    ),
  // Not on the belt (export only), so not on the Dashboard.
  comparisonOnAnotherPage: null,
  supportingPages: (columns) => {
    const count = columns.reduce((n, c) => n + c.gaps.length, 0);
    const byDomain = columns
      .filter((c) => c.gaps.length > 0)
      .sort((a, b) => b.gaps.length - a.gaps.length || a.domain.localeCompare(b.domain));
    return action(
      'supportingPages',
      'topic_ownership',
      'Supporting pages',
      count,
      plural(count, 'PAGE', 'PAGES'),
      columns.length === 1
        ? sentence(columns[0].verdict)
        : `${plural(count, 'competitor page', 'competitor pages')} on this topic ${count === 1 ? 'has' : 'have'} no match on your site.`,
      byDomain.map((c) => `${c.domain} (${plural(c.gaps.length, 'page', 'pages')})`)
    );
  },
  sections: (s) => {
    const sections = s.missing?.rows.length ?? 0;
    const queries = s.rank?.rows.length ?? 0;
    return action(
      'sections',
      'section_gap',
      s.title,
      sections + queries,
      missingSectionsCountLabel(sections, queries) ?? '',
      missingSectionsFact(s.missing?.rivalLabel ?? s.rank?.rivalLabel, sections, queries),
      chips([...(s.missing?.rows ?? []).map((r) => r.label), ...(s.rank?.rows ?? []).map((r) => r.keyword)])
    );
  },
  sectionCompareNotice: (n) =>
    note(
      'sectionCompareNotice',
      'Sections',
      n.kind === 'unreadable'
        ? 'We could not read the page this one is compared with, so no sections were compared.'
        : 'The comparison failed on our side, so no sections were compared.'
    ),
  // Not on the belt (export only), so not on the Dashboard.
  alreadyOnPage: null,
  freshness: (f) =>
    action(
      'freshness',
      'freshness',
      'Freshness',
      1,
      f.label,
      f.verdict ? sentence(f.verdict) : `Your page was last updated ${f.yourPage}.`,
      [
        `yours ${f.yourPage}`,
        f.serpTypical ? `top results ${f.serpTypical.typicalLabel.replace(/^typical\s+/i, 'typically ')}` : ''
      ].filter(Boolean),
      f.tone
    ),
  answerStructure: (a) => {
    const missing = a.vocab?.missing ?? [];
    return action(
      'answerStructure',
      'serp_phrase',
      'Answer structure',
      1,
      missing.length ? plural(missing.length, 'MISSING TERM', 'MISSING TERMS') : 'STRUCTURE',
      a.instruction
        ? // The card's line points at the passages under it ("see … below"); the Dashboard has none.
          sentence(a.instruction.replace(/\s*\(see [^)]*below\)/gi, ''))
        : missing.length
          ? `${plural(missing.length, 'word the top results share is', 'words the top results share are')} not on this page.`
          : 'The top results answer in a different shape from this page.',
      chips(missing)
    );
  },
  pageType: (p) =>
    action(
      'pageType',
      'serp_intent',
      'Page type',
      1,
      p.chipLabel,
      p.briefLines[0]
        ? sentence(p.briefLines[0])
        : `The top results are ${p.serpFamily} pages; this page reads as ${p.yourFamily}.`,
      []
    ),
  technicalIssues: (findings) => technicalRow('technicalIssues', findings),
  technicalWarnings: (findings) => technicalRow('technicalWarnings', findings),
  internalLinks: (rows) =>
    action(
      'internalLinks',
      'internal_link',
      'Internal links',
      rows.length,
      plural(rows.length, 'LINK', 'LINKS'),
      `We found ${plural(rows.length, 'related page', 'related pages')} on your site that could link here and ${
        rows.length === 1 ? 'does' : 'do'
      } not.`,
      chips(
        rows.map((r) => pathOf(r.sourceUrl)),
        DASHBOARD_LINK_MAX_CHIPS
      )
    ),
  cannibalization: (c) => {
    const n = c.pairs.length;
    return action(
      'cannibalization',
      'cannibalization',
      'Cannibalization',
      n,
      n === 1 ? c.pairs[0].verdictLabel.toUpperCase() : `${n} PAIRS`,
      n === 1
        ? sentence(c.pairs[0].action)
        : `${n} other pages of your site compete with this one for the same searches.`,
      // One pair: the fact already names the other page.
      n === 1 ? [] : chips(c.pairs.map((p) => `${p.verdictLabel}: ${p.otherPath}`))
    );
  },
  nearMissSearches: (c) => {
    const n = c.items.length;
    const fact =
      n === 1
        ? sentence(c.items[0].change)
        : `${n} changes to wording or sections, for searches this page already shows for at positions 4 to 20.`;
    return action(
      'nearMissSearches',
      'near_miss_searches',
      NEAR_MISS_CARD_TITLE,
      n,
      plural(n, 'SEARCH CHANGE', 'SEARCH CHANGES'),
      // Without Search Console the card's numbers are estimates; the row says so in three words.
      c.estimated ? `${fact} Positions estimated.` : fact,
      chips(c.items.map((i) => i.searches[0].search))
    );
  },
  signals: (s) => {
    const u = s.unclustered;
    const label = u
      ? u.recommendation.replace(/_/g, ' ').toUpperCase()
      : s.noPlanCluster
        ? 'NO PLAN CLUSTER'
        : (s.labels[0] ?? 'SIGNAL').toUpperCase();
    const fact = u
      ? sentence(u.label)
      : s.noPlanCluster
        ? 'This page is not in any plan cluster.'
        : sentence(s.labels.join(', ') || 'A signal is open on this page');
    return action('signals', 'signals', 'Signals', 1, label, fact, chips(u?.topTopics ?? []));
  },
  // A measured fact with nothing to do: the panel's "Clear" line, worded as the card's one sentence.
  estimateBesideRival: (e) => ({
    card: 'estimateBesideRival',
    key: 'estimate_beside_rival',
    title: 'Estimated traffic',
    label: 'ESTIMATED',
    tone: 'neutral',
    fact: e.sentence,
    specifics: [],
    payload: null,
    count: 1
  })
};

/** Every card key, in `/review`'s order. */
export const REVIEW_CARD_KEYS = Object.keys(DASHBOARD_ROW_BUILDERS) as ReviewCardKey[];

/**
 * The Dashboard's rows for one page: one per card `/review` shows, in `/review`'s order. Triage
 * payloads are the component's (`payload` is null here): they carry the Issues queue's keys.
 */
export function dashboardPageRows(cards: ReviewPageCards): DashboardPageRow[] {
  const rows: DashboardPageRow[] = [];
  for (const key of REVIEW_CARD_KEYS) {
    const build = DASHBOARD_ROW_BUILDERS[key] as ((card: unknown) => DashboardPageRow) | null;
    if (!build || !reviewBeltShowsCard(cards, key)) continue;
    rows.push(build(cards[key]));
  }
  return rows;
}

/**
 * Where the Dashboard's rows and what the belt renders disagree, one line each; empty when they
 * match. Both directions: a belt card without its row, a row for a card the belt does not render
 * (an export-only field, or a card hidden on this page), and a card with more than one row. The
 * expectation is `REVIEW_CARD_ON_BELT`, not the builders, so the check is not the builder asked
 * twice. Lines name cards by key only, so they can go in a PR comment.
 */
export function dashboardRowParity(cards: ReviewPageCards, rows: readonly DashboardPageRow[]): string[] {
  const out: string[] = [];
  const onBelt = REVIEW_CARD_ON_BELT as Record<string, boolean | undefined>;
  const keys = new Set<string>([...Object.keys(cards), ...Object.keys(onBelt)]);
  for (const key of keys) {
    const value = (cards as unknown as Record<string, unknown>)[key];
    if (onBelt[key] === undefined && reviewCardShown(value)) {
      out.push(`card ${key} is not marked as on or off the belt`);
    }
    const shown = onBelt[key] === true && reviewCardShown(value);
    const n = rows.filter((r) => r.card === key).length;
    if (shown && n === 0) out.push(`card ${key} has no Dashboard row`);
    else if (!shown && n > 0) out.push(`Dashboard row ${key} has no card on /review`);
    else if (n > 1) out.push(`card ${key} has ${n} Dashboard rows`);
  }
  for (const r of rows) {
    if (!keys.has(r.card)) out.push(`Dashboard row ${r.card} is not a card`);
  }
  return out;
}
